import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { HomeLink } from "../components/HomeLink";
import { ThemeToggle } from "../components/ThemeToggle";
import { Button } from "../components/ui/Button";
import { UsernameForm } from "../components/UsernameForm";
import { authClient, type AuthIdentity } from "../lib/auth";
import { resolveRedirectTarget } from "../lib/redirect";
import { useNavigate } from "../lib/router-context";
import { useAppStore } from "../lib/store";
import { getThemeColors } from "../lib/theme";

type SocialProvider = "twitch" | "discord";

export function LoginPage() {
  const identity = useAppStore((state) => state.auth);
  const setIdentity = useAppStore((state) => state.setAuthIdentity);
  const isDark = useAppStore((state) => state.isDark);
  const toggleTheme = useAppStore((state) => state.toggleTheme);
  const theme = getThemeColors(isDark);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  const redirectTarget = resolveRedirectTarget(window.location.search);

  useEffect(() => {
    if (identity && !identity.isAnonymous) {
      navigate(redirectTarget);
    }
  }, [identity, redirectTarget]);

  if (!identity) return null;

  const run = async (
    key: string,
    action: () => Promise<AuthIdentity>,
  ): Promise<boolean> => {
    setPending(key);
    setError(null);
    try {
      setIdentity(await action());
      navigate(redirectTarget);
      return true;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("login.error"));
      return false;
    } finally {
      setPending(null);
    }
  };

  const signInWith = async (provider: SocialProvider) => {
    setPending(provider);
    setError(null);
    const result = await authClient.signIn.social({
      provider,
      callbackURL: `${window.location.origin}${redirectTarget}`,
      errorCallbackURL: `${window.location.origin}/login`,
    });
    if (result?.error) {
      setError(result.error.message || t("login.error"));
      setPending(null);
    }
  };

  return (
    <div className={`min-h-screen ${theme.bg}`}>
      <header className="w-full">
        <div className="w-full px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <HomeLink />
            <div className="flex items-center gap-4">
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
            </div>
          </div>
        </div>
      </header>

      <main className="pt-8 pb-24">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1
              className={`text-5xl font-light tracking-tight md:text-6xl ${theme.text} mb-6`}
            >
              {t("login.title")}
            </h1>
            <p className={`${theme.textMuted} mx-auto mb-12 max-w-xl text-lg`}>
              {t("login.subtitle")}
            </p>
          </div>

          <div className="mx-auto max-w-md">
            <div className="space-y-3">
              <Button
                data-testid="login-twitch-btn"
                type="button"
                variant="primary"
                isDark={isDark}
                disabled={pending !== null}
                aria-busy={pending === "twitch"}
                onClick={() => void signInWith("twitch")}
              >
                {pending === "twitch" ? (
                  <span
                    aria-hidden="true"
                    className={`h-5 w-5 animate-spin rounded-full border-2 border-transparent ${isDark ? "border-t-black" : "border-t-white"}`}
                  />
                ) : (
                  <svg
                    className="h-5 w-5 shrink-0"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M6 0 1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0H6Zm14.571 11.143-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714v9.429ZM11.571 4.714h1.715v5.143h-1.715V4.714Zm4.715 0H18v5.143h-1.714V4.714Z" />
                  </svg>
                )}
                {t("login.twitch")}
              </Button>
              <Button
                data-testid="login-discord-btn"
                type="button"
                variant="secondary"
                isDark={isDark}
                disabled={pending !== null}
                aria-busy={pending === "discord"}
                onClick={() => void signInWith("discord")}
              >
                {pending === "discord" ? (
                  <span
                    aria-hidden="true"
                    className={`h-5 w-5 animate-spin rounded-full border-2 border-transparent ${isDark ? "border-t-white" : "border-t-gray-900"}`}
                  />
                ) : (
                  <svg
                    className="h-5 w-5 shrink-0"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.865-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.319 13.58.099 18.058a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.873-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .078-.01c3.928 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .079.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.128 12.3 12.3 0 0 1-1.873.891.077.077 0 0 0-.041.107c.36.698.772 1.363 1.225 1.993a.076.076 0 0 0 .084.029 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .031-.055c.5-5.177-.838-9.674-3.548-13.66a.061.061 0 0 0-.031-.03ZM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418Zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418Z" />
                  </svg>
                )}
                {t("login.discord")}
              </Button>
            </div>

            <div className={`my-6 flex items-center gap-3 ${theme.textMuted}`}>
              <span
                aria-hidden="true"
                className="h-0.5 flex-1 bg-ink/40"
              />
              <span className="text-sm">{t("login.or")}</span>
              <span
                aria-hidden="true"
                className="h-0.5 flex-1 bg-ink/40"
              />
            </div>

            <UsernameForm
              identity={identity}
              isDark={isDark}
              pending={pending}
              onSubmit={run}
            />

            <p
              data-testid="login-scores-hint"
              className={`mt-6 text-center text-sm ${theme.textMuted}`}
            >
              {t("login.scoresHint")}
            </p>

            {error && (
              <p
                data-testid="login-error"
                role="alert"
                className={`mt-4 text-center text-sm ${theme.errorText}`}
              >
                {error}
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
